import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiCamera, FiMapPin, FiCheckCircle, FiX, FiAlertCircle } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import MapView from '../components/MapView';
import { enrollAPI } from '../services/api';
import { toast } from 'react-toastify';

const EnrollPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const enrollToken = searchParams.get('t');
  const [status, setStatus] = useState('loading');
  const [immo, setImmo] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    responsable: '',
    description: '',
    etat: 'bon',
    photo: null,
    photoPreview: null,
    latitude: null,
    longitude: null,
  });

  const [gpsStatus, setGpsStatus] = useState('searching');

  useEffect(() => {
    checkEnrollStatus();
    captureGPS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const checkEnrollStatus = async () => {
    try {
      const res = await enrollAPI.check(id);
      const data = res.data.data;
      if (data.enrolle) {
        const infoRes = await enrollAPI.getInfo(id);
        setImmo(infoRes.data.data);
        setStatus('info');
      } else {
        setImmo(data);
        if (data.nom) setFormData((prev) => ({ ...prev, nom: data.nom }));
        setStatus('enroll');
      }
    } catch {
      setStatus('error');
    }
  };

  const captureGPS = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setFormData((prev) => ({
            ...prev,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }));
          setGpsStatus('located');
        },
        () => setGpsStatus('error'),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGpsStatus('error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onload = (e) => setFormData((prev) => ({ ...prev, photoPreview: e.target.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!formData.nom) return toast.error('Le nom est obligatoire');
    setEnrolling(true);
    try {
      const data = new FormData();
      data.append('nom', formData.nom);
      data.append('etat', formData.etat);
      if (formData.responsable) data.append('responsable', formData.responsable);
      if (formData.description) data.append('notes', formData.description);
      if (formData.latitude) data.append('latitude', formData.latitude);
      if (formData.longitude) data.append('longitude', formData.longitude);
      if (formData.photo) data.append('photo', formData.photo);
      await enrollAPI.enroll(id, data, enrollToken);
      setSuccess(true);
      toast.success('Enrôlement réussi !');
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de l'enrôlement");
    } finally {
      setEnrolling(false);
    }
  };

  // === Header partagé OGP ===
  const PageHeader = () => (
    <div className="enroll-header">
      <div className="enroll-badge">
        <span className="enroll-badge-text">OGP</span>
      </div>
      <h1 className="enroll-title">Office Guinéen de Publicité</h1>
      <p className="enroll-subtitle">Inventaire des immobilisations</p>
      <div className="enroll-divider" />
    </div>
  );

  // === LOADING ===
  if (status === 'loading') {
    return (
      <div className="enroll-page enroll-page-center">
        <LoadingSpinner />
      </div>
    );
  }

  // === ERROR ===
  if (status === 'error') {
    return (
      <div className="enroll-page enroll-page-center">
        <div className="enroll-card enroll-card-status">
          <FiAlertCircle className="enroll-icon-error" />
          <h2 className="enroll-card-title">Immobilisation introuvable</h2>
          <p className="enroll-card-text">
            Ce QR code ne correspond à aucune immobilisation enregistrée.
          </p>
        </div>
      </div>
    );
  }

  // === SUCCESS ===
  if (success) {
    return (
      <div className="enroll-page enroll-page-center">
        <div className="enroll-card enroll-card-status">
          <FiCheckCircle className="enroll-icon-success" />
          <h2 className="enroll-card-title">Enrôlement réussi</h2>
          <p className="enroll-card-text">
            L'immobilisation <strong>{formData.nom}</strong> a été enregistrée.
          </p>
          {formData.latitude && (
            <p className="enroll-gps-success">
              <FiMapPin /> Position GPS enregistrée
            </p>
          )}
        </div>
      </div>
    );
  }

  // === ENROLLMENT FORM ===
  if (status === 'enroll') {
    return (
      <div className="enroll-page">
        <div className="enroll-container">
          <PageHeader />

          <div className="enroll-card">
            <div className={`enroll-gps-banner enroll-gps-${gpsStatus}`}>
              <FiMapPin />
              {gpsStatus === 'located' && `Position capturée (${formData.latitude?.toFixed(4)}, ${formData.longitude?.toFixed(4)})`}
              {gpsStatus === 'searching' && 'Recherche de la position GPS…'}
              {gpsStatus === 'error' && 'GPS non disponible'}
            </div>

            <form onSubmit={handleEnroll} className="enroll-form">
              <div className="enroll-field">
                <label>Nom de l'immobilisation *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  placeholder="Ex: Bureau directeur, Véhicule Toyota…"
                />
              </div>

              <div className="enroll-field">
                <label>Responsable</label>
                <input
                  type="text"
                  value={formData.responsable}
                  onChange={(e) => setFormData({ ...formData, responsable: e.target.value })}
                  placeholder="Nom du responsable"
                />
              </div>

              <div className="enroll-field">
                <label>État</label>
                <select
                  value={formData.etat}
                  onChange={(e) => setFormData({ ...formData, etat: e.target.value })}
                >
                  <option value="bon">Bon état</option>
                  <option value="moyen">Moyen</option>
                  <option value="hors_service">Hors service</option>
                </select>
              </div>

              <div className="enroll-field">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description ou observations…"
                  rows={3}
                />
              </div>

              <div className="enroll-field">
                <label><FiCamera style={{ display: 'inline', marginRight: 4 }} /> Photo</label>
                {formData.photoPreview ? (
                  <div className="enroll-photo-preview">
                    <img src={formData.photoPreview} alt="Aperçu" />
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, photo: null, photoPreview: null }))}
                      className="enroll-photo-remove"
                      aria-label="Retirer la photo"
                    >
                      <FiX />
                    </button>
                  </div>
                ) : (
                  <label className="enroll-photo-drop">
                    <FiCamera />
                    <span>Appuyez pour prendre une photo</span>
                    <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} />
                  </label>
                )}
              </div>

              <button type="submit" className="enroll-submit" disabled={enrolling}>
                {enrolling ? 'Enrôlement en cours…' : "Valider l'enrôlement"}
              </button>
            </form>
          </div>

          <p className="enroll-footer">Office Guinéen de Publicité — OGP IMMO</p>
        </div>
      </div>
    );
  }

  // === INFO DISPLAY (ALREADY ENROLLED) ===
  if (status === 'info' && immo) {
    const fields = [
      ['N° Identification', immo.numero_identification],
      ['Catégorie', immo.categorie_nom],
      ['Service', immo.service_nom],
      ['Responsable', immo.responsable],
      ['Valeur', immo.valeur ? `${Number(immo.valeur).toLocaleString()} GNF` : null],
      ['Enrôlé le', immo.date_enrolement ? new Date(immo.date_enrolement).toLocaleDateString('fr-FR') : null],
      ['Nombre de scans', immo.scan_count],
    ].filter(([, v]) => v);

    const etatLabel = immo.etat === 'hors_service' ? 'Hors service' : immo.etat === 'moyen' ? 'Moyen' : 'Bon';
    const etatClass = immo.etat === 'hors_service' ? 'error' : immo.etat === 'moyen' ? 'warning' : 'success';

    return (
      <div className="enroll-page">
        <div className="enroll-container enroll-container-wide">
          <PageHeader />

          <div className="enroll-card">
            <div className="enroll-info-head">
              <h2>{immo.nom}</h2>
              <span className={`enroll-badge-state enroll-badge-state-${etatClass}`}>{etatLabel}</span>
            </div>

            {immo.photo_url && (
              <img src={immo.photo_url} alt={immo.nom} className="enroll-info-photo" />
            )}

            <dl className="enroll-info-list">
              {fields.map(([label, value]) => (
                <div key={label} className="enroll-info-row">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>

            {immo.description && (
              <div className="enroll-info-desc">
                <h3>Description</h3>
                <p>{immo.description}</p>
              </div>
            )}

            {immo.latitude && immo.longitude && (
              <div className="enroll-info-map">
                <h3><FiMapPin /> Localisation</h3>
                <MapView
                  immobilisations={[immo]}
                  center={[immo.latitude, immo.longitude]}
                  height="250px"
                />
              </div>
            )}
          </div>

          <p className="enroll-footer">Office Guinéen de Publicité — OGP IMMO</p>
        </div>
      </div>
    );
  }

  return null;
};

export default EnrollPage;
